import React, { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { MdClose } from "react-icons/md";
import { IoCalendarClearOutline, IoLocationOutline, IoPeopleOutline, IoPersonOutline } from "react-icons/io5";
import { TbTicket } from "react-icons/tb";
import { AiOutlineUserAdd } from "react-icons/ai";
import { useSelector } from "react-redux";
import EAModal from "../../shared/Modal/EAModal";
import EAArticle from "../../shared/EAArticle/EAArticle";
import EAButton from "../../shared/Buttons/EAButton";
import EAAlert from "../../shared/EAAlert";
import EARadio from "../../shared/Radio/EARadio";
import EAChip from "../../shared/Chip/EAChip";
import LoadableComponent from "../../../../features/Loading/LoadableComponent";
import ExerciseImageHeader from "../../ScheduleExercise/single-exercise/SingleExerciseDetailsModal/ExerciseImageHeader";
import TicketPurchaseContent from "../../ScheduleExercise/single-exercise/SingleExerciseDetailsModal/TicketPurchaseContent";
import { SwimmingLesson } from "../../../models/SwimmingScheduleData";
import { Exercise } from "../../../models/Exercise";
import { Ticket } from "../../../models/Ticket";
import { setTicketAccessFromExercise } from "../../../models/TicketAccess";
import useGetTicketAndPasses from "../../../../utils/hooks/useGetTicketAndPasses";
import useExerciseInfo from "../../ScheduleExercise/exerciseInfo/useExerciseInfo";
import useApiRequest from "../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../utils/hooks/useFacility";
import getId from "../../../../utils/getId";
import { State } from "../../../store";
import { JOIN_TO_EXERCISE } from "../../../api/apiRoutes";
import { isValidityEndingSoon } from "../../../utils/TicketPassHelper";
import { differenceInDays } from "date-fns";
import { errorMessageFromApi } from "../../../utils/apiErrorMessages";
import "./style.scss";

type ModalStep = "info" | "details" | "pass-selector" | "ticket-purchase";

interface IProps {
  lesson: SwimmingLesson | null;
  isOpen: boolean;
  onClose: () => void;
}


const SwimmingLessonModal: React.FC<IProps> = ({ lesson, isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState<ModalStep>("info");
  const [selectedPassId, setSelectedPassId] = useState<string>("");
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showReadMoreButton, setShowReadMoreButton] = useState(false);
  const [joinError, setJoinError] = useState("");
  const descriptionRef = useRef<HTMLElement>(null);

  const { tickets, getTicketsByExercise, loading: ticketsLoading } = useGetTicketAndPasses();
  const { availablePasses, loading: passesLoading } = useExerciseInfo();
  const { post } = useApiRequest();
  const facilityIds = useFacility();
  const userDataId = useSelector((state: State) => state.auth.userData.objectId);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep("info");
      setSelectedPassId("");
      setIsDescriptionExpanded(false);
      setJoinError("");
    }
  }, [isOpen, lesson]);

  useEffect(() => {
    if (isOpen && lesson?.exerciseGroup?.objectId) {
      getTicketsByExercise(lesson.exerciseGroup.objectId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lesson]);

  useEffect(() => {
    if (descriptionRef.current && lesson?.description) {
      const lineHeight = parseInt(window.getComputedStyle(descriptionRef.current).lineHeight);
      const maxHeight = lineHeight * 3;
      setShowReadMoreButton(descriptionRef.current.scrollHeight > maxHeight);
    }
  }, [lesson?.description, isOpen]);

  if (!lesson) return null;

  const isSemester = lesson.courseType === "SwimmingLessonSemester";
  const isFull = lesson.reserved >= lesson.capacity;
  const isPast = lesson.startTime ? lesson.startTime < Date.now() : false;
  const isUnavailable = lesson.status === "I" || isFull || isPast;
  const durationMinutes = lesson.startTime && lesson.closeTime
    ? Math.round((lesson.closeTime - lesson.startTime) / 60000)
    : 0;
  const instructorName = [lesson.humanName, lesson.humanLastName].filter(Boolean).join(" ");
  const deputyName = [lesson.deputyHumanName, lesson.deputyHumanLastName].filter(Boolean).join(" ");
  const hasTrainer = !!instructorName || !!deputyName;
  const hasLocation = !!lesson.location?.trim();

  const lowestPrice = tickets.length > 0 ? Math.min(...tickets.map((t) => t.price)) : null;

  const ticketAccess = setTicketAccessFromExercise(lesson.objectId, lesson.name, lesson.startTime);

  const lessonAsExercise = {
    objectId: lesson.objectId,
    name: lesson.name,
    startTime: lesson.startTime,
    closeTime: lesson.closeTime,
    capacity: lesson.capacity,
    reserved: lesson.reserved,
    prereservation: lesson.prereservation ?? 0,
    zone: { displayName: lesson.location ?? "" },
    humanName: instructorName,
    deputyHumanIds: { objectId: 0, objectDb: 0 },
  } as Exercise;

  const joinToExercise = async (assignType: boolean, passId: string) => {
    setJoinError("");
    const response = await post(JOIN_TO_EXERCISE(facilityIds, passId), {
      exerciseIds: lesson.objectId,
      webUserIds: `0,${userDataId}`,
      assigned: assignType,
    });
    if (!response.isError) {
      onClose();
      return;
    }
    setJoinError(errorMessageFromApi(response));
  };

  const lessonImageData = {
    name: lesson.name,
    groupName: lesson.exerciseGroupName,
    durationMinutes,
    capacity: lesson.capacity,
    reserved: lesson.reserved,
    imageUrl: lesson.eventMessage,
  };

  // ─── Info box (2×2 grid + description) ──────────────────────────────────────

  const renderInfoBox = () => (
    <div className="swimming-modal-info-box">
      <div className="swimming-modal-info-box__grid">
        <div className="swimming-modal-info-box__item">
          <IoCalendarClearOutline className="swimming-modal-info-box__item-icon" />
          <div className="swimming-modal-info-box__item-content">
            <span className="swimming-modal-info-box__item-label">Data i godzina</span>
            <span className="swimming-modal-info-box__item-value">
              {lesson.startTime_str}–{lesson.closeTime_str}
            </span>
          </div>
        </div>

        {hasLocation && (
          <div className="swimming-modal-info-box__item">
            <IoLocationOutline className="swimming-modal-info-box__item-icon" />
            <div className="swimming-modal-info-box__item-content">
              <span className="swimming-modal-info-box__item-label">Strefa</span>
              <span className="swimming-modal-info-box__item-value">{lesson.location}</span>
            </div>
          </div>
        )}

        <div className="swimming-modal-info-box__item">
          <IoPeopleOutline className="swimming-modal-info-box__item-icon" />
          <div className="swimming-modal-info-box__item-content">
            <span className="swimming-modal-info-box__item-label">Zajęte miejsca</span>
            <span className="swimming-modal-info-box__item-value">
              {lesson.reserved}/{lesson.capacity}
            </span>
          </div>
        </div>

        {hasTrainer && (
          <div className="swimming-modal-info-box__item">
            <IoPersonOutline className="swimming-modal-info-box__item-icon" />
            <div className="swimming-modal-info-box__item-content">
              <div className="swimming-modal-info-box__item-label-row">
                <span className="swimming-modal-info-box__item-label">Trener</span>
                {deputyName && <EAChip label="Zastępstwo" variant="warning" />}
              </div>
              <span className="swimming-modal-info-box__item-value">
                {deputyName ? (
                  <>
                    {instructorName && <s>{instructorName}</s>}
                    {instructorName && deputyName && " "}
                    {deputyName}
                  </>
                ) : (
                  instructorName
                )}
              </span>
            </div>
          </div>
        )}
      </div>

      {lesson.description && (
        <div className="swimming-modal-info-box__description">
          <article
            ref={descriptionRef}
            className={`swimming-modal-info-box__description-content${isDescriptionExpanded ? " swimming-modal-info-box__description-content--expanded" : ""}`}
            dangerouslySetInnerHTML={{ __html: lesson.description }}
          />
          {showReadMoreButton && (
            <button
              className="swimming-modal-info-box__read-more"
              onClick={() => setIsDescriptionExpanded((prev) => !prev)}
            >
              {isDescriptionExpanded ? "Zwiń" : "Czytaj więcej"}
            </button>
          )}
        </div>
      )}
    </div>
  );

  // ─── Info step (two-column layout) ──────────────────────────────────────────

  const renderInfoStep = () => (
    <EAModal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      hideButtons
      isCloseButton={false}
      desktopWidth="1000px"
    >
      <div className="swimming-modal-container">
        <div className="swimming-modal-container__left">
          <ExerciseImageHeader lessonData={lessonImageData} />
        </div>

        <div className="swimming-modal-container__right">
          <div className="swimming-modal-container__header">
            <h4 className="swimming-modal-container__title">{lesson.name}</h4>
            <MdClose
              size={28}
              className="swimming-modal-container__close"
              onClick={onClose}
            />
          </div>

          <div className="swimming-modal-mobile-image">
            <ExerciseImageHeader lessonData={lessonImageData} />
            <MdClose
              size={24}
              className="swimming-modal-mobile-image__close"
              onClick={onClose}
            />
          </div>

          {renderInfoBox()}

          <div className="swimming-modal-container__actions">
            {isUnavailable ? (
              <EAChip
                variant="default"
                label={
                  isPast
                    ? "Te zajęcia już się odbyły."
                    : isFull
                      ? "Brak wolnych miejsc na te zajęcia."
                      : "Zajęcia są obecnie niedostępne."
                }
              />
            ) : isSemester ? (
              <>
                <EAButton
                  title="Zobacz szczegóły kursu"
                  fullWidth
                  onClick={() => setCurrentStep("details")}
                />
                {lowestPrice !== null && (
                  <p className="swimming-modal-container__price-caption">
                    Ceny zaczynają się od {lowestPrice} zł
                  </p>
                )}
              </>
            ) : (
              <>
                <EAButton
                  title="Zapisz się karnetem"
                  fullWidth
                  startIcon={<AiOutlineUserAdd />}
                  onClick={() => setCurrentStep("pass-selector")}
                />
                <EAButton
                  title="Kup jednorazowy bilet"
                  fullWidth
                  variant="outlined"
                  startIcon={<TbTicket />}
                  onClick={() => setCurrentStep("ticket-purchase")}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </EAModal>
  );

  // ─── Details step (semester) ─────────────────────────────────────────────────

  const renderDetailsStep = () => (
    <EAModal
      isOpen={isOpen}
      onClose={onClose}
      title="W ramach kursu"
      onBack={() => setCurrentStep("info")}
      hideButtons
      desktopWidth="660px"
    >
      <div className="swimming-modal-details">
        {lesson.description && (
          <EAArticle content={lesson.description} maxHeight="calc(60vh - 140px)" />
        )}

        <div className="swimming-modal-details__footer">
          <EAButton
            title="Powrót"
            variant="outlined"
            onClick={() => setCurrentStep("info")}
          />
          <EAButton
            title="Przejdź do wyboru karnetu"
            onClick={() => setCurrentStep("ticket-purchase")}
          />
        </div>
      </div>
    </EAModal>
  );

  // ─── Pass selector step ──────────────────────────────────────────────────────

  const getValidPasses = (): Ticket[] =>
    availablePasses.filter(
      (p) => p.ticket === false && p.validfrom <= lesson.startTime && lesson.startTime <= p.validto,
    );

  const getPassChip = (pass: Ticket) => {
    const remaining = pass.entrances - pass.exercises.length;
    if (remaining <= 0) return <EAChip label="Wyczerpano" variant="error" />;
    if (isValidityEndingSoon(pass)) {
      return <EAChip label={`Wygasa za ${differenceInDays(pass.validto, new Date())} dni`} variant="warning" />;
    }
    return null;
  };

  const handleJoinWithPass = () => {
    joinToExercise(false, selectedPassId || "null");
  };

  const renderPassSelectorStep = () => {
    const validPasses = getValidPasses();

    return (
      <EAModal
        isOpen={isOpen}
        onClose={onClose}
        title="Wybierz karnet"
        onBack={() => { setCurrentStep("info"); setSelectedPassId(""); }}
        hideButtons
        desktopWidth="480px"
      >
        <LoadableComponent loading={passesLoading}>
          <div className="swimming-pass-selector">
            {validPasses.length === 0 ? (
              <>
                <EAAlert
                  type="gray"
                  message="Nie masz aktywnych karnetów uprawniających do zapisu na te zajęcia."
                />
                <EAButton
                  title="Powrót"
                  variant="outlined"
                  fullWidth
                  onClick={() => setCurrentStep("info")}
                />
              </>
            ) : (
              <>
                <div className="swimming-pass-selector__info-chips">
                  <EAChip label="Formularz zgłoszeniowy uzupełnisz w dalszym etapie" variant="primary" />
                  <EAChip label="Uczestnika kursu wskażesz w formularzu" variant="primary" />
                  {isSemester && <EAChip label="Karnet obejmuje cały semestr zajęć" variant="primary" />}
                </div>
                <div className="swimming-pass-selector__list">
                  {validPasses.map((pass) => {
                    const passId = getId(pass);
                    const remaining = pass.entrances - pass.exercises.length;
                    return (
                      <div
                        key={passId}
                        className={`swimming-pass-selector__item ${selectedPassId === passId ? "swimming-pass-selector__item--selected" : ""}`}
                        onClick={() => setSelectedPassId(passId)}
                      >
                        <EARadio
                          name="pass"
                          value={passId}
                          checked={selectedPassId === passId}
                          onChange={setSelectedPassId}
                        />
                        <div className="swimming-pass-selector__item-info">
                          <span className="swimming-pass-selector__item-name">{pass.name}</span>
                          <span className="swimming-pass-selector__item-meta">
                            Ważny do: {format(pass.validto, "dd.MM.yyyy")} &nbsp;·&nbsp; Wejść: {remaining}/{pass.entrances}
                          </span>
                        </div>
                        {getPassChip(pass)}
                      </div>
                    );
                  })}
                </div>
                <EAButton
                  title="Zapisz się"
                  fullWidth
                  startIcon={<AiOutlineUserAdd />}
                  disabled={!selectedPassId}
                  onClick={handleJoinWithPass}
                />
                {joinError && <EAAlert type="error" message={joinError} />}
              </>
            )}
          </div>
        </LoadableComponent>
      </EAModal>
    );
  };

  // ─── Ticket purchase step ────────────────────────────────────────────────────

  const renderTicketPurchaseStep = () => (
    <EAModal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      hideButtons
      isCloseButton={false}
      desktopWidth="660px"
    >
      <LoadableComponent loading={ticketsLoading}>
        {!ticketsLoading && tickets.length === 0 ? (
          <div className="swimming-pass-selector">
            <EAAlert
              type="gray"
              message="Brak dostępnych karnetów dla tych zajęć. Skontaktuj się z obiektem."
            />
            <EAButton
              title="Powrót"
              variant="outlined"
              fullWidth
              onClick={() => setCurrentStep("info")}
            />
          </div>
        ) : (
          <TicketPurchaseContent
            exercise={lessonAsExercise}
            tickets={tickets}
            ticketAccess={ticketAccess}
            onBack={() => setCurrentStep("info")}
            onClose={onClose}
            aboveTickets={
              <div className="swimming-pass-selector__info-chips">
                <EAChip label="„Formularz zgłoszeniowy uzupełnisz w dalszym etapie" variant="primary" />
                <EAChip label="Uczestnika kursu wskażesz w formularzu" variant="primary" />
                {isSemester && <EAChip label="Karnet obejmuje cały semestr zajęć" variant="primary" />}
              </div>
            }
          />
        )}
      </LoadableComponent>
    </EAModal>
  );

  switch (currentStep) {
    case "info":
      return renderInfoStep();
    case "details":
      return renderDetailsStep();
    case "pass-selector":
      return renderPassSelectorStep();
    case "ticket-purchase":
      return renderTicketPurchaseStep();
    default:
      return null;
  }
};

export default SwimmingLessonModal;
